import {DistributorReference, Filter, ItemsForUserVO} from "../../models/table-models";
import {Pagination} from "../../utils/pagination-hooks";
import {useTranslation} from "react-i18next";
import {Accordion} from "react-bootstrap";
import Card from "react-bootstrap/Card";
import {NoDataComponent} from "../../components/NoDataComponent";
import {formatQuantitiesByPassport} from "../../utils/helper-functions";
import {CustomTable} from "../../components/CustomTable";
import {
    referencesDistributorInnerTableHeaders,
    referencesDistributorTableHeaders
} from "../../utils/Enums/TableHeaders";
import React from "react";

export const UserList = ({
                             references,
                             pagination,
                             filter,
                             refetchDistributorReferencesHook,
                         }: {
    references: ItemsForUserVO[];
    filter: Filter;
    pagination: Pagination;
    refetchDistributorReferencesHook: () => void;
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'common'});

    const calculateTotals = (references: ItemsForUserVO[], field: 'usedItems' | 'soldItems') => {
        const itemTotals: DistributorReference[] = [];

        references.forEach(data => {
            if (data.date != null) {
                (data[field] ?? []).forEach(item => {
                    const existingItem = itemTotals.find(
                        (total) => total.passportId === item.passportId && total.batchCode === item.batchCode
                    );
                    if (existingItem) {
                        existingItem.count += item.count;
                        if (item.oilAmount != null) {
                            existingItem.oilAmount = (existingItem.oilAmount ?? 0) + item.oilAmount;
                        }
                        existingItem.collectionOfItems?.push(item);
                    } else {
                        itemTotals.push({
                            passportId: item.passportId,
                            passportName: item.passportName,
                            batchCode: item.batchCode,
                            count: item.count,
                            state: item.state,
                            oilAmount: item.oilAmount,
                            collectionOfItems: [item],
                        });
                    }
                });
            }
        });

        return itemTotals;
    };

    const totalUsedItemsData = calculateTotals(references, 'usedItems');
    const totalSoldItemsData = calculateTotals(references, 'soldItems');

    return references.length > 0 ? (
        <div>
        <Accordion className='tasks accordion flex-grow-1 w-100'>
            {references.map((data, index) =>
                data.date != null ? (
                    <Accordion.Item key={index}
                                    className='w-100 py-0'
                                    eventKey={`${data.date}`}
                                    draggable={false}
                                    onDragOver={(ev) => ev.preventDefault()}
                                    onDragStart={undefined}
                                    onDrop={undefined}
                    >
                        <Accordion.Header className='w-100 p-1'>
                            <div className='d-flex align-items-center text-center w-100'>
                                <h5 className='mb-0 px-3'>{data.date}</h5>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body className='d-flex pb-3 pt-1 w-100'>
                            <ShowReferences
                                items={data}
                                filter={filter}
                                pagination={pagination}
                                refetchDistributorReferencesHook={refetchDistributorReferencesHook}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                    ) : (
                    <div key={index} className='d-flex'>
                        <ShowReferences
                            items={data}
                            filter={filter}
                            pagination={pagination}
                            refetchDistributorReferencesHook={refetchDistributorReferencesHook}
                        />
                    </div>
                )
            )}
        </Accordion>
        <Card key={"last"} className='w-100 py-0'>
            <Card.Header className='w-100 p-1'>
                <div className='d-flex align-items -center text-center w-100'>
                    <h5 className='mb-0 px-3'>{t("total")}</h5>
                </div>
            </Card.Header>
            <Accordion.Collapse eventKey={`last`} className='d-flex pb-3 pt-1 w-100'>
                <div className='row g-0 w-100'>
                    <div className='col-6 w-auto'>
                        <DistributorItems
                            {...{pagination, filter}}
                            list={totalUsedItemsData}
                            refetchEntitiesHook={refetchDistributorReferencesHook}
                            heading={"usedItems"}
                        />
                    </div>
                    {totalSoldItemsData.length > 0 && (
                        <div className='col-6 w-auto'>
                            <DistributorItems
                                {...{pagination, filter}}
                                list={totalSoldItemsData}
                                refetchEntitiesHook={refetchDistributorReferencesHook}
                                heading={"soldItems"}
                            />
                        </div>
                    )}
                </div>
            </Accordion.Collapse>
        </Card>
        </div>
    ) : (
        <NoDataComponent noDataText={t('noInventoryReference')}/>
    );
};

export const DistributorItems = ({
                              refetchEntitiesHook,
                              list,
                              heading,
                          }: {
    refetchEntitiesHook: () => void;
    list: DistributorReference[];
    heading: string;
}) => {
    const {t} = useTranslation('translation', {keyPrefix: 'common'});

    const formatQuantitiesByPassportDR = (items: DistributorReference[]) => {
        return formatQuantitiesByPassport(
            items,
            (item) => item.passportId,
            (existingItem, newItem) => {
                existingItem.count += newItem.count;
                if (newItem.oilAmount != null) {
                    existingItem.oilAmount = (existingItem.oilAmount ?? 0) + newItem.oilAmount;
                }
                existingItem.collectionOfItems?.push(newItem);
            }
        );
    }

    return (
        <div className={'m-3'}>
            <h5 className='mb-2 d-flex justify-content-center text-center w-100'>{t(`${heading}`)}</h5>
            <CustomTable
                columns={{
                    headings: referencesDistributorTableHeaders,
                    innerTableFields: [{field: 'passportName'}, {field: 'batchCode'}, {field: 'state'}, {field: 'count'}],
                    collectionOfItems: list.map(item => ({
                        ...item,
                        state: t(item.state === 'AVAILABLE' && heading === 'defectedItemsFromBranches' ? 'FIT' : item.state ? item.state : ''),
                        count: (item.oilAmount != null ? `${item.count} (${item.oilAmount} ml)` : item.count) as any,
                    })),
                    collapsedHeadings: referencesDistributorInnerTableHeaders,
                    groupByField: (item) => item?.passportId.toString(),
                    items: formatQuantitiesByPassportDR(list).map(({passportName, batchCode, state, count, oilAmount}) => [
                        passportName,
                        t(state === 'AVAILABLE' && heading === 'defectedItemsFromBranches' ? 'FIT' : state ? state : ''),
                        oilAmount != null ? `${count} (${oilAmount} ml)` : count,
                    ]),
                }}
                tableData={formatQuantitiesByPassportDR(list)}
                hasAccordionRow={true}
                {...{refetchEntitiesHook}}
                tableFooterText={(item) => {
                    return item.oilAmount != null
                        ? `${t('quantity')}: ${item.count} (${item.oilAmount} ml)`
                        : `${t('quantity')}: ${item.count.toString()}`;
                }}
            />
        </div>
    );
};

const ShowReferences = ({
                            pagination,
                            filter,
                            refetchDistributorReferencesHook,
                            items,
                        }: {
    items: ItemsForUserVO;
    filter: Filter;
    pagination: Pagination;
    refetchDistributorReferencesHook: () => void;
}) => {
    const headings = Object.keys(items).slice(1);
    return (
        <div className={'row g-3 m-0'}>
            {Object.values(items)
                .slice(1)
                .map((value, index) => {
                    return (
                        value &&
                        value.length !== 0 && (
                            <div key={index} className='col-6 w-auto'>
                                <DistributorItems
                                    {...{pagination, filter}}
                                    list={value}
                                    refetchEntitiesHook={refetchDistributorReferencesHook}
                                    heading={headings[index] === 'scrap' ? 'inventoryScrap' : headings[index]}
                                />
                            </div>
                        )
                    );
                })}
        </div>
    );
};
